import { supabase } from '@/lib/supabase/client';
import { createNotFoundError, fromPostgrestError, normalizeError } from '@/lib/services/errors';
import type { Database } from '@/lib/supabase/types.gen';
import type {
  PostgrestError,
  PostgrestMaybeSingleResponse,
  PostgrestResponse,
  PostgrestSingleResponse,
} from '@supabase/postgrest-js';
import type {
  PaginationInput,
  PortfolioUnitsResult,
  PortfolioUnitSummary,
  UnitListMetrics,
  UnitFormValues,
  UnitListFilters,
  UnitSummary,
  UnitsListResult,
  UnitPossessionTimeline,
} from '@/lib/types/property';
import {
  createUnitListMetrics,
  LEASEABLE_UNIT_STATUSES,
  mapUnitRowToFormValues,
  mapUnitRowToSummary,
  type UnitRow,
} from '@/lib/supabase/unitMappers';
import { createInternalUnitCode } from '@/lib/utils/unitIdentifiers';

type UnitsTable = Database['public']['Tables']['units'];
type UnitInsert = UnitsTable['Insert'];
type UnitUpdate = UnitsTable['Update'];
type PropertiesTable = Database['public']['Tables']['properties'];
type PropertyRow = PropertiesTable['Row'];
function normalizePossessionTimeline(value: unknown): UnitPossessionTimeline {
  return value === 'negotiable' ? 'negotiable' : 'immediate';
}
export interface ListUnitsParams {
  propertyId?: string;
  filters?: UnitListFilters;
  signal?: AbortSignal;
}

export interface ListPortfolioUnitsParams {
  propertyId?: string;
  filters?: UnitListFilters;
  signal?: AbortSignal;
  page?: number;
  pageSize?: number;
}
export async function listUnits(params: ListUnitsParams = {}): Promise<UnitsListResult> {
  try {
    let query = createUnitsQuery()
      .select('*')
      .order('floor_level', { ascending: true, nullsFirst: true })
      .order('unit_code', { ascending: true });

    const scopedPropertyId = params.filters?.propertyId ?? params.propertyId;
    if (typeof scopedPropertyId === 'string' && scopedPropertyId.length > 0) {
      query = query.eq('property_id', scopedPropertyId);
    }

    const floorLevels = params.filters?.floorLevels;
    if (Array.isArray(floorLevels) && floorLevels.length > 0) {
      query = query.in('floor_level', floorLevels);
    }

    const readiness = params.filters?.readiness;
    if (Array.isArray(readiness) && readiness.length > 0) {
      query = query.in('availability_status', readiness);
    }

    const displayOnPublicWebsite = params.filters?.displayOnPublicWebsite;
    if (typeof displayOnPublicWebsite === 'boolean') {
      query = query.eq('display_on_public_website', displayOnPublicWebsite);
    }

    const published = params.filters?.published;
    if (typeof published === 'boolean') {
      query = query.eq('published', published);
    }

    if (params.signal) {
      query = query.abortSignal(params.signal);
    }

    const response = (await query.throwOnError()) as PostgrestResponse<UnitRow>;
    const rows: UnitRow[] = Array.isArray(response.data) ? response.data : [];
    const items = rows.map(mapUnitRowToSummary);
    const metrics = createUnitListMetrics(rows);

    return {
      items,
      metrics,
      totalItems: items.length,
      totalPages: 1,
      page: 1,
      pageSize: items.length,
    };
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function listPortfolioUnits(
  params: ListPortfolioUnitsParams = {},
): Promise<PortfolioUnitsResult> {
  try {
    const pagination = sanitizePagination(params.page, params.pageSize);
    const from = (pagination.page - 1) * pagination.pageSize;
    const to = from + pagination.pageSize - 1;

    let query = createUnitsQuery()
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);

    const scopedPropertyId = params.filters?.propertyId ?? params.propertyId;
    if (typeof scopedPropertyId === 'string' && scopedPropertyId.length > 0) {
      query = query.eq('property_id', scopedPropertyId);
    }

    const floorLevels = params.filters?.floorLevels;
    if (Array.isArray(floorLevels) && floorLevels.length > 0) {
      query = query.in('floor_level', floorLevels);
    }

    const readiness = params.filters?.readiness;
    if (Array.isArray(readiness) && readiness.length > 0) {
      query = query.in('availability_status', readiness);
    }

    const displayOnPublicWebsite = params.filters?.displayOnPublicWebsite;
    if (typeof displayOnPublicWebsite === 'boolean') {
      query = query.eq('display_on_public_website', displayOnPublicWebsite);
    }

    const published = params.filters?.published;
    if (typeof published === 'boolean') {
      query = query.eq('published', published);
    }

    if (params.signal) {
      query = query.abortSignal(params.signal);
    }

    const response = (await query.throwOnError()) as PostgrestResponse<UnitRow>;
    const { data, count } = response;
    const rows: UnitRow[] = Array.isArray(data) ? data : [];
    const baseSummaries = rows.map(mapUnitRowToSummary);
    const propertyIds = Array.from(
      new Set(
        rows
          .map((row) => row.property_id)
          .filter((value): value is string => typeof value === 'string' && value.length > 0),
      ),
    );
    const propertyMetadata = await fetchPropertyMetadata(propertyIds);

    const items: PortfolioUnitSummary[] = baseSummaries.map((summary) => ({
      ...summary,
      propertyCode: propertyMetadata[summary.propertyId]?.code,
      propertyLabel: propertyMetadata[summary.propertyId]?.label,
    }));

    const totalItems = typeof count === 'number' ? count : items.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pagination.pageSize));
    const metrics = await fetchPortfolioUnitMetrics({
      filters: params.filters,
      propertyId: params.propertyId,
      signal: params.signal,
      totalItems,
    });

    return {
      items,
      totalItems,
      totalPages,
      page: pagination.page,
      pageSize: pagination.pageSize,
      metrics,
    };
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function getUnit(propertyId: string, unitId: string): Promise<UnitFormValues> {
  try {
    const row = await fetchUnitRow(propertyId, unitId);
    return mapUnitRowToFormValues(row);
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function getUnitSummary(propertyId: string, unitId: string): Promise<UnitSummary> {
  try {
    const row = await fetchUnitRow(propertyId, unitId);
    return mapUnitRowToSummary(row);
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function createUnit(
  propertyId: string,
  values: UnitFormValues,
  userId: string,
): Promise<UnitFormValues> {
  try {
    const payload = mapUnitFormToInsert(propertyId, values, userId);
    const response = (await createUnitsQuery()
      .insert(payload)
      .select('*')
      .single()
      .throwOnError()) as PostgrestSingleResponse<UnitRow>;
    if (!response.data) {
      throw createNotFoundError('Unable to create unit.');
    }
    return mapUnitRowToFormValues(response.data);
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function updateUnit(
  propertyId: string,
  unitId: string,
  values: UnitFormValues,
  userId: string,
): Promise<UnitFormValues> {
  try {
    const existing = await fetchUnitRow(propertyId, unitId);
    const payload = mapUnitFormToUpdate(values, userId, existing);
    const response = (await createUnitsQuery()
      .update(payload)
      .eq('id', unitId)
      .eq('property_id', propertyId)
      .select('*')
      .single()
      .throwOnError()) as PostgrestSingleResponse<UnitRow>;

    if (!response.data) {
      throw createNotFoundError('Unit not found.');
    }

    return mapUnitRowToFormValues(response.data);
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function archiveUnit(
  propertyId: string,
  unitId: string,
  userId: string,
): Promise<UnitSummary> {
  try {
    const response = (await createUnitsQuery()
      .update({ display_on_public_website: false, published: false, updated_by: userId })
      .eq('property_id', propertyId)
      .eq('id', unitId)
      .select('*')
      .single()
      .throwOnError()) as PostgrestSingleResponse<UnitRow>;

    if (!response.data) {
      throw createNotFoundError('Unit not found.');
    }

    return mapUnitRowToSummary(response.data);
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function deleteUnit(propertyId: string, unitId: string): Promise<void> {
  try {
    const response = (await createUnitsQuery()
      .delete()
      .eq('property_id', propertyId)
      .eq('id', unitId)
      .select('id')
      .single()
      .throwOnError()) as PostgrestSingleResponse<Pick<UnitRow, 'id'>>;
    const { data } = response;

    if (!data || typeof data.id !== 'string') {
      throw createNotFoundError('Unit not found.');
    }
  } catch (error) {
    handleSupabaseError(error);
  }
}

async function fetchUnitRow(propertyId: string, unitId: string): Promise<UnitRow> {
  const response = (await createUnitsQuery()
    .select('*')
    .eq('property_id', propertyId)
    .eq('id', unitId)
    .maybeSingle()
    .throwOnError()) as PostgrestMaybeSingleResponse<UnitRow>;
  const { data } = response;

  if (!data) {
    throw createNotFoundError('Unit not found.');
  }

  return data;
}

function mapUnitFormToInsert(
  propertyId: string,
  values: UnitFormValues,
  userId: string,
): UnitInsert {
  const normalizedUnitCode = values.unitCode.trim();
  const unitCode =
    typeof normalizedUnitCode === 'string' && normalizedUnitCode.length > 0
      ? normalizedUnitCode
      : createInternalUnitCode();
  const availabilityStart = normalizeAvailabilityStart(values.availabilityStart);
  return {
    property_id: propertyId,
    unit_code: unitCode,
    unit_label: values.unitLabel,
    floor_level: values.floorLevel ?? null,
    area_sf: values.areaSf,
    min_divisible_sf: values.minDivisibleSf ?? null,
    availability_start: availabilityStart ?? getDateOnly(new Date()),
    availability_end: null,
    availability_status: values.availabilityStatus,
    rent_basis: values.rentBasis,
    net_rent_per_sf: values.netRentPerSf,
    additional_rent_per_sf: values.additionalRentPerSf ?? null,
    rent_frequency: values.rentFrequency,
    incentives: values.incentives ?? null,
    potentially_divisible: values.potentiallyDivisible,
    contact_email: values.contactEmail,
    gallery_image_urls: values.galleryImageUrls ?? [],
    hero_image_url: values.heroImageUrl ?? null,
    plan_geometry: values.planGeometry ?? null,
    possession_timeline: normalizePossessionTimeline(values.possessionTimeline),
    display_on_public_website: values.displayOnPublicWebsite,
    published: values.published,
    status_updated_at: new Date().toISOString(),
    created_by: userId,
    updated_by: userId,
  };
}

function mapUnitFormToUpdate(
  values: UnitFormValues,
  userId: string,
  existing: UnitRow,
): UnitUpdate {
  const didStatusChange = values.availabilityStatus !== existing.availability_status;
  const normalizedUnitCode = values.unitCode.trim();
  const unitCode =
    typeof normalizedUnitCode === 'string' && normalizedUnitCode.length > 0
      ? normalizedUnitCode
      : existing.unit_code;
  const availabilityStart = normalizeAvailabilityStart(values.availabilityStart);
  return {
    unit_code: unitCode,
    unit_label: values.unitLabel,
    floor_level: values.floorLevel ?? null,
    area_sf: values.areaSf,
    min_divisible_sf: values.minDivisibleSf ?? null,
    availability_start: availabilityStart ?? existing.availability_start,
    availability_status: values.availabilityStatus,
    rent_basis: values.rentBasis,
    net_rent_per_sf: values.netRentPerSf,
    additional_rent_per_sf: values.additionalRentPerSf ?? null,
    rent_frequency: values.rentFrequency,
    incentives: values.incentives ?? null,
    potentially_divisible: values.potentiallyDivisible,
    contact_email: values.contactEmail,
    gallery_image_urls: values.galleryImageUrls ?? existing.gallery_image_urls,
    hero_image_url: values.heroImageUrl ?? null,
    plan_geometry: values.planGeometry ?? null,
    possession_timeline: normalizePossessionTimeline(values.possessionTimeline),
    display_on_public_website: values.displayOnPublicWebsite,
    published: values.published,
    status_updated_at: didStatusChange ? new Date().toISOString() : undefined,
    updated_by: userId,
  } satisfies UnitUpdate;
}

function normalizeAvailabilityStart(value?: string | null): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getDateOnly(value: Date): string {
  return value.toISOString().slice(0, 10);
}

async function fetchPropertyMetadata(
  propertyIds: string[],
): Promise<Partial<Record<string, { code?: string; label?: string }>>> {
  if (propertyIds.length === 0) {
    return {};
  }

  const response = (await createPropertiesQuery()
    .select(
      'id, property_code, street_number, street_name, street_type, street_direction, suite_number, city, province, postal_code',
    )
    .in('id', propertyIds)
    .throwOnError()) as PostgrestResponse<PropertyRow>;

  const rows: PropertyRow[] = Array.isArray(response.data) ? response.data : [];

  const map: Partial<Record<string, { code?: string; label?: string }>> = {};
  for (const row of rows) {
    if (typeof row.id !== 'string' || row.id.length === 0) {
      continue;
    }
    map[row.id] = {
      code: row.property_code,
      label: formatPropertyLabel(row),
    };
  }

  return map;
}

function formatPropertyLabel(row: PropertyRow): string {
  const parts = [
    [row.street_number, row.street_name, row.street_type].filter(Boolean).join(' '),
    row.city,
  ].filter(Boolean);
  return parts.join(', ');
}

async function fetchPortfolioUnitMetrics(params: {
  filters?: UnitListFilters;
  propertyId?: string;
  signal?: AbortSignal;
  totalItems: number;
}): Promise<UnitListMetrics> {
  if (params.totalItems === 0) {
    return { total: 0, published: 0, leaseable: 0 };
  }

  let publishedQuery = createUnitsQuery()
    .select('id', { head: true, count: 'exact' })
    .eq('published', true);
  let leaseableQuery = createUnitsQuery()
    .select('id', { head: true, count: 'exact' })
    .eq('display_on_public_website', true)
    .in('availability_status', LEASEABLE_UNIT_STATUSES);

  const scopedPropertyId = params.filters?.propertyId ?? params.propertyId;
  if (typeof scopedPropertyId === 'string' && scopedPropertyId.length > 0) {
    publishedQuery = publishedQuery.eq('property_id', scopedPropertyId);
    leaseableQuery = leaseableQuery.eq('property_id', scopedPropertyId);
  }

  const floorLevels = params.filters?.floorLevels;
  if (Array.isArray(floorLevels) && floorLevels.length > 0) {
    publishedQuery = publishedQuery.in('floor_level', floorLevels);
    leaseableQuery = leaseableQuery.in('floor_level', floorLevels);
  }

  const readiness = params.filters?.readiness;
  if (Array.isArray(readiness) && readiness.length > 0) {
    publishedQuery = publishedQuery.in('availability_status', readiness);
    leaseableQuery = leaseableQuery.in('availability_status', readiness);
  }

  const displayOnPublicWebsiteFilter = params.filters?.displayOnPublicWebsite;
  if (typeof displayOnPublicWebsiteFilter === 'boolean') {
    publishedQuery = publishedQuery.eq('display_on_public_website', displayOnPublicWebsiteFilter);
    leaseableQuery = leaseableQuery.eq('display_on_public_website', displayOnPublicWebsiteFilter);
  }

  const publishedFilter = params.filters?.published;
  if (typeof publishedFilter === 'boolean') {
    publishedQuery = publishedQuery.eq('published', publishedFilter);
    leaseableQuery = leaseableQuery.eq('published', publishedFilter);
  }

  if (params.signal) {
    publishedQuery = publishedQuery.abortSignal(params.signal);
    leaseableQuery = leaseableQuery.abortSignal(params.signal);
  }

  const publishedResponse = (await publishedQuery.throwOnError()) as PostgrestResponse<
    Pick<UnitRow, 'id'>
  >;
  const leaseableResponse = (await leaseableQuery.throwOnError()) as PostgrestResponse<
    Pick<UnitRow, 'id'>
  >;
  const publishedCount = typeof publishedResponse.count === 'number' ? publishedResponse.count : 0;
  const leaseableCount = typeof leaseableResponse.count === 'number' ? leaseableResponse.count : 0;

  return {
    total: params.totalItems,
    published: publishedCount,
    leaseable: leaseableCount,
  };
}

function sanitizePagination(page?: number, pageSize?: number): PaginationInput {
  const safePage =
    typeof page === 'number' && Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  const safeSize =
    typeof pageSize === 'number' && Number.isFinite(pageSize) && pageSize > 0
      ? Math.floor(pageSize)
      : 25;
  return {
    page: safePage,
    pageSize: Math.min(safeSize, 200),
  };
}

function handleSupabaseError(error: unknown): never {
  if (isPostgrestError(error)) {
    throw fromPostgrestError(error);
  }
  throw normalizeError(error);
}

function isPostgrestError(error: unknown): error is PostgrestError {
  return typeof error === 'object' && error !== null && 'code' in error && 'message' in error;
}

function createUnitsQuery() {
  return supabase.from('units');
}

function createPropertiesQuery() {
  return supabase.from('properties');
}
